import type { AnalysisState } from '@aibi/contracts';
import { cn } from '@aibi/ui';
import { useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { cardHeight, cardRect, cardWidth } from '../../model/workspace-document';
import { useWorkspaceActions } from '../../state/workspace-context';
import { useCardResizeSession } from './card-resize-session';
import {
  buildPreview,
  collectSiblingRects,
  EDGE_CURSOR,
  EDGE_LABEL,
  measureResizeMetrics,
  rectOf,
  RESIZE_EDGES,
  settlingPreview,
  type CardRect,
  type CardSizePreview,
  type ResizeEdge,
  type ResizeMetrics,
} from './card-resize';

export type { CardSizePreview };

interface DragState {
  readonly edge: ResizeEdge;
  readonly pointerId: number;
  readonly startPointer: { readonly x: number; readonly y: number };
  readonly startRect: CardRect;
  readonly metrics: ResizeMetrics;
  readonly others: readonly CardRect[];
  readonly overlayOrigin: { readonly left: number; readonly top: number };
  readonly commitWidth: number;
  readonly commitHeight: number;
  readonly commitX: number;
  readonly commitY: number;
}

const SETTLE_MS = 180;

const isCorner = (edge: ResizeEdge): boolean => edge.length === 2;
const affects = (edge: ResizeEdge, side: 'n' | 's' | 'e' | 'w'): boolean => edge.includes(side);

/**
 * Asas de redimension estilo Figma en los 4 lados y esquinas. El arrastre es continuo en
 * px; al soltar anima al snap de rejilla y confirma `resize-card` (incluye x/y si crece
 * hacia la izquierda o arriba).
 */
export function CardResizeHandles({
  card,
  cardRef,
  disabled = false,
  visible = false,
  onPreview,
}: {
  readonly card: AnalysisState;
  readonly cardRef: RefObject<HTMLElement | null>;
  readonly disabled?: boolean;
  readonly visible?: boolean;
  readonly onPreview: (preview: CardSizePreview | null) => void;
}) {
  const { execute } = useWorkspaceActions();
  const session = useCardResizeSession();
  const drag = useRef<DragState | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [active, setActive] = useState<ResizeEdge | null>(null);

  if (disabled) return null;

  const clearGuides = () => {
    session?.setGuides([]);
  };

  const start = (edge: ResizeEdge, event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    const element = cardRef.current;
    const grid = element?.parentElement ?? null;
    const overlay = grid?.parentElement ?? null;
    if (element === null || grid === null || overlay === null) return;
    event.preventDefault();
    event.stopPropagation();
    if (settleTimer.current !== null) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
    const metrics = measureResizeMetrics(grid);
    const overlayBox = overlay.getBoundingClientRect();
    const gridPos = cardRect(card);
    drag.current = {
      edge,
      pointerId: event.pointerId,
      startPointer: { x: event.clientX, y: event.clientY },
      startRect: rectOf(element),
      metrics,
      others: collectSiblingRects(element),
      overlayOrigin: { left: overlayBox.left, top: overlayBox.top },
      commitWidth: cardWidth(card),
      commitHeight: cardHeight(card),
      commitX: gridPos.x,
      commitY: gridPos.y,
    };
    setActive(edge);
    const initial = buildPreview({
      edge,
      start: drag.current.startRect,
      pointer: drag.current.startPointer,
      startPointer: drag.current.startPointer,
      metrics,
      others: drag.current.others,
      overlayOrigin: drag.current.overlayOrigin,
    });
    onPreview(initial);
    session?.setGuides(initial.guides);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const move = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    if (state?.pointerId !== event.pointerId) return;
    const next = buildPreview({
      edge: state.edge,
      start: state.startRect,
      pointer: { x: event.clientX, y: event.clientY },
      startPointer: state.startPointer,
      metrics: state.metrics,
      others: state.others,
      overlayOrigin: state.overlayOrigin,
    });
    onPreview(next);
    session?.setGuides(next.guides);
  };

  const end = (event: ReactPointerEvent<HTMLButtonElement>, commit: boolean) => {
    const state = drag.current;
    if (state?.pointerId !== event.pointerId) return;
    const next = buildPreview({
      edge: state.edge,
      start: state.startRect,
      pointer: { x: event.clientX, y: event.clientY },
      startPointer: state.startPointer,
      metrics: state.metrics,
      others: state.others,
      overlayOrigin: state.overlayOrigin,
    });
    drag.current = null;
    setActive(null);
    clearGuides();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const nextX = affects(state.edge, 'w')
      ? state.commitX + state.commitWidth - next.width
      : state.commitX;
    const nextY = affects(state.edge, 'n')
      ? state.commitY + state.commitHeight - next.height
      : state.commitY;
    const unchanged =
      next.width === state.commitWidth &&
      next.height === state.commitHeight &&
      nextX === state.commitX &&
      nextY === state.commitY;
    if (!commit || unchanged) {
      onPreview(null);
      return;
    }

    onPreview(settlingPreview(next, state.metrics));
    settleTimer.current = setTimeout(() => {
      settleTimer.current = null;
      execute({
        type: 'resize-card',
        cardId: card.id,
        width: next.width,
        height: next.height,
        x: nextX,
        y: nextY,
      });
      onPreview(null);
    }, SETTLE_MS);
  };

  return (
    <>
      {RESIZE_EDGES.map((edge) => (
        <button
          key={edge}
          type="button"
          aria-label={EDGE_LABEL[edge]}
          className={cn(
            'absolute z-20 flex touch-none items-center justify-center p-2 transition-[opacity,transform] duration-150',
            EDGE_CURSOR[edge],
            visible || active !== null
              ? 'opacity-100'
              : 'opacity-0 group-hover/card:opacity-100 group-focus-within/card:opacity-100',
            active === edge && 'scale-110',
            edge === 'n' && 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
            edge === 's' && 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
            edge === 'e' && 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
            edge === 'w' && 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
            edge === 'ne' && 'right-0 top-0 translate-x-1/2 -translate-y-1/2',
            edge === 'nw' && 'left-0 top-0 -translate-x-1/2 -translate-y-1/2',
            edge === 'se' && 'right-0 bottom-0 translate-x-1/2 translate-y-1/2',
            edge === 'sw' && 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2',
          )}
          onPointerDown={(event) => {
            start(edge, event);
          }}
          onPointerMove={move}
          onPointerUp={(event) => {
            end(event, true);
          }}
          onPointerCancel={(event) => {
            end(event, false);
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <span
            aria-hidden
            className={cn(
              'block rounded-[2px] border-2 border-primary bg-background shadow-sm',
              isCorner(edge) ? 'size-2.5' : 'size-2',
              active === edge && 'bg-primary',
            )}
          />
        </button>
      ))}
    </>
  );
}
